import React, { useEffect } from 'react';
import { get, map } from 'lodash';

import { AppSkeleton, DashboardTable, Graph } from '@/component';
import { ButtonLoader, Dropdown } from '@/kaya';
import { userActions } from '@/actions';
import { useArtistStore } from '@/store';
import { dashboardTypes } from '@/types';

import styles from './Dashboard.module.scss';

const numberFormatter = new Intl.NumberFormat(undefined);
const moneyFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const EMPTY_DASHBOARD: dashboardTypes.Dashboard = {
  change_in_likes: 0,
  change_in_views: 0,
  topDislikedSongs: [],
  topLikedSongs: [],
  total_likes: 0,
  total_songs: 0,
  total_views: 0,
  viewData: [],
};

const Dashboard = () => {
  const { artist } = useArtistStore();
  const artistId = get(artist, 'id');

  const [dashboardData, setDashboardData] =
    React.useState<dashboardTypes.Dashboard>(EMPTY_DASHBOARD);
  const [filter, setFilter] = React.useState({
    value: 'DAILY',
    label: 'Daily',
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const requestIdRef = React.useRef(0);

  // const ref = React.useRef(false);
  const options = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'HALF_YEARLY', label: 'Half Yearly' },
    { value: 'START_YEAR', label: 'Start Year' },
    { value: 'YEARLY', label: 'Yearly' },
  ];

  useEffect(() => {
    if (!artistId) return;

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await userActions.fetchDashboardAction({
          artist_id: artistId,
          filter: filter.value,
        });

        if (cancelled || requestId !== requestIdRef.current) return;

        setDashboardData(res.success ? (res.data as dashboardTypes.Dashboard) : EMPTY_DASHBOARD);
      } catch (e) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setDashboardData(EMPTY_DASHBOARD);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [filter.value, artistId]);

  // const totalRevenue = dashboardData.total_views * 0.003;// check for not a number
  const totalRevenue = Number.isFinite(dashboardData.total_views)
    ? dashboardData.total_views * 0.003
    : 0;
  const totalRevenueText = `$${moneyFormatter.format(totalRevenue)}`;

  const changeTotalView = dashboardData?.change_in_views;
  const changeTotalLikes = dashboardData?.change_in_likes;
  const showViewChange = dashboardData.total_views !== 0 && Number.isFinite(changeTotalView);
  const showLikesChange = dashboardData.total_likes !== 0 && Number.isFinite(changeTotalLikes);

  const viewData = map(dashboardData?.viewData, (item) => {
    const date = 'date' in item ? item.date : item._id;
    const total = 'totalLog' in item ? item.totalLog : item.total;

    return {
      date,
      total_logs: Number.isFinite(total) ? total : 0,
    };
  }).filter((d) => Boolean(d.date));

  // if (isLoading) {
  //   return <Loader isLoading />;
  // }

  return (
    <AppSkeleton>
      {!isLoading ? (
        <div className={styles.Dashboard}>
          <div className={styles.heading}>
            <h1 className={styles.title}>Artist Dashboard</h1>
            <Dropdown
              options={options}
              className={styles.Dropdown}
              // placeholder="Daily"
              onMenuItemClick={(e) => {
                setFilter({ value: e.value.toString(), label: e.label });
              }}
              value={filter}
            />
          </div>
          <div className={styles.detailBox}>
            <div className={styles.box1}>
              <p className={styles.subTitle}>Total Songs </p>
              <p className={styles.currency}>{numberFormatter.format(dashboardData.total_songs)}</p>
            </div>

            {/* <div className={styles.box2}>
            <p className={styles.subTitle}>Listening Hours</p>
            <div className={styles.currency}>
              <p className={styles.value}>24</p>
              <p className={styles.change}>+ Hr</p>
            </div>
          </div> */}
            <div className={styles.box2}>
              <p className={styles.subTitle}>Total Revenue</p>
              <div className={styles.currency}>
                <p className={styles.value}>{totalRevenueText}</p>
                {/* <p className={styles.change}>+ 36%</p> */}
              </div>
            </div>
            <div className={styles.box2}>
              <p className={styles.subTitle}>Total Views</p>
              <div className={styles.currency}>
                <p className={styles.value}>{numberFormatter.format(dashboardData.total_views)}</p>
                <p
                  className={styles.change}
                  style={
                    showViewChange && changeTotalView !== 0
                      ? { color: changeTotalView > 0 ? 'green' : 'red' }
                      : undefined
                  }
                >
                  {showViewChange ? `${changeTotalView.toPrecision(3)}%` : ''}
                </p>
              </div>
            </div>
            <div className={styles.box2}>
              <p className={styles.subTitle}>Total Likes</p>
              <div className={styles.currency}>
                <p className={styles.value}>{numberFormatter.format(dashboardData.total_likes)}</p>
                <p
                  className={styles.change}
                  style={
                    showLikesChange && changeTotalLikes !== 0
                      ? { color: changeTotalLikes > 0 ? 'green' : 'red' }
                      : undefined
                  }
                >
                  {showLikesChange ? `${changeTotalLikes.toPrecision(3)}%` : ''}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.graphContainerMain}>
            <div className={styles.conntainer1}>
              <p className={styles.title}>Views</p>
              <div className={styles.graphContainer}>
                <Graph data={viewData} graphFilter={filter.value} type="View" />
              </div>
            </div>
            <div className={styles.conntainer2}>
              <p className={styles.title}>Earnings</p>
              <div className={styles.graphContainer}>
                <Graph data={viewData} graphFilter={filter.value} type="Earning" />
              </div>
            </div>
          </div>
          <div className={styles.tableContainer}>
            <DashboardTable classname={styles.table1} data={dashboardData} />

            <DashboardTable type="down" data={dashboardData} />
          </div>
        </div>
      ) : (
        <div className={styles.ButtonLoaderContriner}>
          <ButtonLoader active className={styles.ButtonLoader} />
        </div>
      )}
    </AppSkeleton>
  );
};

export default Dashboard;
